//******************************************************************************************
// SettingsPopup Component: Displays and manages configuration settings for the application.
// Allows an admin to view, create, and update settings, including toggling bot activation,
// schedules, and holidays. Organized into three tabs for clarity.
//******************************************************************************************

import React, { useState, useEffect } from "react";
import "./SettingsPopup.scss";
import axios from "axios";
import { useAuthContext } from "../../../context/AuthContext";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
// Icons
import { FaCogs, FaClock, FaCalendarAlt } from "react-icons/fa";
/**
 * Formats a raw number to euro format by dividing by 1e9
 * and adding the euro symbol.
 * @param {number} value
 * @returns {string} -
 */
const formatEuro = (value) => {
  return `${(value / 1e9).toFixed(2).replace(".", ",")}€`;
};

/** Helper to format a Date -> "YYYY-MM-DD" */
const formatDate = (date) => {
  if (!date) return "";

  // If date is already in YYYY-MM-DD format, return it
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // Parse date and extract components in UTC to avoid timezone issues
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const SettingsPopup = ({ onClose }) => {
  const { authUser } = useAuthContext();
  // Check if the user can edit config
  const canEditConfig = authUser?.isSuperAdmin || authUser?.canChangeConfig;

  // Which tab is active
  // If super admin, we start on "superadmin", else "schedule".
  const [activeTab, setActiveTab] = useState(authUser?.isSuperAdmin ? "superadmin" : "schedule");

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("  ");

  const [confirmationState, setConfirmationState] = useState({});

  const [holidays, setHolidays] = useState([]);
  const [editedHolidays, setEditedHolidays] = useState([]);

  const [closeUntilDate, setCloseUntilDate] = useState("");

  const [usage, setUsage] = useState({ used: 0, total: 1 });
  const [usagePercentage, setUsagePercentage] = useState(0);
  useEffect(() => {
    fetchUsage();
  }, []);
  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem("admin-token");
      if (!token) {
        console.error("No admin token found.");
        return;
      }

      const apiUrl = new URL("/api/config/usage?type=ai_spend", process.env.REACT_APP_API_URL).toString(); // Replace with your API endpoint
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const { value, quota } = response.data.data.usages?.[0] || {};
        const percentage = quota ? (value / quota) * 100 : 0; // Prevent division by zero
        setUsage({
          used: value || 0,
          total: quota || 1, // Avoid division by zero
        });
        setUsagePercentage(percentage.toFixed(2)); // Ensure percentage is calculated and rounded
      } else {
        console.warn("Failed to fetch usage data.");
      }
    } catch (error) {
      console.error("Error fetching usage data:", error);
    }
  };
  useEffect(() => {
    fetchHolidays();
  }, []);
  useEffect(() => {
    const { used, total } = usage;
    const percentage = ((used / total) * 100).toFixed(2);
    setUsagePercentage(percentage);
  }, [usage]);
  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem("admin-token");
      const apiUrl = new URL("/api/config/holidays", process.env.REACT_APP_API_URL).toString();
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const data = response.data.holidays.map((holiday) => ({
          ...holiday,
          date: formatDate(holiday.date),
        }));
        setHolidays(data);
        setEditedHolidays(data);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  const fetchDefaultHolidays = async () => {
    try {
      const token = localStorage.getItem("admin-token");
      const currentYear = new Date().getFullYear();
      const apiUrl = new URL(`/api/config/holidays/${currentYear}`, process.env.REACT_APP_API_URL).toString();

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const data = response.data.holidays.map((holiday) => ({
          ...holiday,
          date: formatDate(holiday.date),
        }));
        setHolidays(data);
        setEditedHolidays(data);
      }
    } catch (error) {
      console.error("Error fetching default holidays:", error);
    }
  };

  const saveHolidays = async () => {
    try {
      const token = localStorage.getItem("admin-token");
      const apiUrl = new URL("/api/config/holidays", process.env.REACT_APP_API_URL).toString();
      const response = await axios.put(
        apiUrl,
        { holidays: editedHolidays },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setMessage("Holidays updated successfully.");
        fetchHolidays(); // Refresh data
      } else {
        setMessage("Failed to update holidays.");
      }
    } catch (error) {
      console.error("Error saving holidays:", error);
    }
  };

  const resetToDefaultHolidays = async () => {
    try {
      setMessage("");
      await fetchDefaultHolidays();
      setMessage("Configuração padrão carregada.");
    } catch (error) {
      setMessage("Falha ao carregar configuração padrão.");
    }
  };

  const updateHoliday = (index, field, value) => {
    const updated = [...editedHolidays];
    updated[index] = { ...updated[index], [field]: value };
    setEditedHolidays(updated);
  };
  const getFixedHolidays = () => editedHolidays.filter((holiday) => holiday.type === "F");
  const getVariableHolidays = () => editedHolidays.map((holiday, index) => ({ holiday, index })).filter(({ holiday }) => holiday.type === "V");
  const getLocalHolidays = () => editedHolidays.map((holiday, index) => ({ holiday, index })).filter(({ holiday }) => holiday.type === "M");
  const getTolerances = () => editedHolidays.map((holiday, index) => ({ holiday, index })).filter(({ holiday }) => holiday.type === "T");

  const handleAddTolerance = () => {
    const newTolerance = { date: "", name: "", type: "T" };
    setEditedHolidays([...editedHolidays, newTolerance]);
  };

  const handleRemoveHoliday = (index) => {
    const updated = editedHolidays.filter((_, i) => i !== index);
    setEditedHolidays(updated);
  };

  const handleExecuteTask = async (taskType) => {
    if (confirmationState[taskType]?.confirmed) {
      // Execute
      setLoading(true);
      setMessage("");

      try {
        const token = localStorage.getItem("admin-token");
        const apiUrl = new URL(`/api/tasks/${taskType}`, process.env.REACT_APP_API_URL).toString();
        const response = await axios.post(apiUrl, {}, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) {
          setMessage(`${taskType === "close" ? "Closing" : "Deletion"} task executed successfully.`);
        } else {
          setMessage(`Failed to execute ${taskType === "close" ? "closing" : "deletion"} task.`);
        }
      } catch (error) {
        console.error(`Error executing ${taskType} task:`, error);
        setMessage(`Failed to execute ${taskType === "close" ? "closing" : "deletion"} task.`);
      }

      setLoading(false);
      setConfirmationState((prev) => ({ ...prev, [taskType]: null }));
    } else {
      startCountdown(taskType);
    }
  };

  const startCountdown = (taskType) => {
    setConfirmationState((prev) => ({
      ...prev,
      [taskType]: { countdown: 3, confirmed: false },
    }));
    const interval = setInterval(() => {
      setConfirmationState((prev) => {
        const curr = prev[taskType]?.countdown || 0;
        if (curr <= 1) {
          clearInterval(interval);
          return {
            ...prev,
            [taskType]: { countdown: 0, confirmed: true },
          };
        }
        return {
          ...prev,
          [taskType]: { countdown: curr - 1, confirmed: false },
        };
      });
    }, 1000);
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem("admin-token");
        if (!token) {
          console.error("No admin token found.");
          return;
        }

        const apiUrl = new URL("/api/config", process.env.REACT_APP_API_URL).toString();
        const response = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          const freshConfig = response.data.config;

          // If closeUntil is in the future, display it
          if (freshConfig.closeUntil) {
            const dateVal = new Date(freshConfig.closeUntil);
            if (dateVal > new Date()) {
              setCloseUntilDate(dateVal.toISOString().split("T")[0]);
            } else {
              setCloseUntilDate("");
            }
          }

          setConfig({
            ...freshConfig,
            workingHours: freshConfig.workingHours || [],
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setConfig(null);
        } else {
          console.error("Error fetching configuration:", error);
        }
      }
    };
    fetchConfig();
  }, [authUser]);

  // decimal <-> HH:MM
  const decimalTimeToHHMM = (decimalTime) => {
    const hours = Math.floor(decimalTime);
    const minutes = Math.round((decimalTime - hours) * 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };
  const hhmmToDecimalTime = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    return h + m / 60;
  };

  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    if (!config) return;
    const decimalValue = hhmmToDecimalTime(value);
    setConfig((prev) => ({ ...prev, [name]: decimalValue }));
  };

  const handleWorkingHoursChange = (e, index, position) => {
    if (!config) return;
    const decimalValue = hhmmToDecimalTime(e.target.value);
    setConfig((prev) => {
      const newWH = [...(prev.workingHours || [])];
      newWH[index][position] = decimalValue;
      return { ...prev, workingHours: newWH };
    });
  };

  const addTimeRange = () => {
    if (!config) return;
    setConfig((prev) => ({
      ...prev,
      workingHours: [...(prev.workingHours || []), [8.0, 17.0]],
    }));
  };

  const removeTimeRange = (index) => {
    if (!config) return;
    setConfig((prev) => {
      const filtered = (prev.workingHours || []).filter((_, i) => i !== index);
      return { ...prev, workingHours: filtered };
    });
  };
  const handleUpdateConfig = async () => {
    if (!config) return;
    setLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("admin-token");
      if (!token) {
        console.error("No admin token found.");
        setLoading(false);
        return;
      }

      // Copy config for payload
      const payload = { ...config };

      // If user can edit config, set closeUntil from the date picker; else remove it
      if (canEditConfig) {
        if (closeUntilDate) {
          payload.closeUntil = new Date(closeUntilDate);
        } else {
          payload.closeUntil = null;
        }
      } else {
        delete payload.closeUntil;
      }

      // If not super admin => remove these
      if (!authUser.isSuperAdmin) {
        delete payload.botActive;
        delete payload.closingTime;
        delete payload.deletionTime;
      }

      const apiUrl = new URL("/api/config", process.env.REACT_APP_API_URL).toString();
      const response = await axios.put(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        if (response.data.message === "No changes made to the configuration.") {
          setMessage("Nenhuma alteração foi feita nas configurações.");
        } else {
          setMessage("Configurações alteradas com sucesso");
        }

        // If the updated config has a future closeUntil, reflect it
        if (response.data.config?.closeUntil) {
          const dateVal = new Date(response.data.config.closeUntil);
          if (dateVal > new Date()) {
            setCloseUntilDate(dateVal.toISOString().split("T")[0]);
          } else {
            setCloseUntilDate("");
          }
        }
      } else {
        setMessage("Falha ao alterar configurações.");
      }
    } catch (error) {
      console.error("Error updating configuration:", error);
      setMessage("Falha ao alterar configurações.");
    }

    setLoading(false);
  };

  const handleCreateDefaultConfig = async () => {
    setLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("admin-token");
      if (!token) {
        console.error("No admin token found.");
        setLoading(false);
        return;
      }

      const apiUrl = new URL("/api/config/create", process.env.REACT_APP_API_URL).toString();
      const response = await axios.post(apiUrl, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setConfig(response.data.config);
        setMessage("Default configuration created successfully.");
      }
    } catch (error) {
      console.error("Error creating configuration:", error);
      setMessage("Failed to create configuration.");
    }

    setLoading(false);
  };

  if (config === null) {
    return (
      <div className="settings-popup">
        <div className="settings-popup__overlay" onClick={onClose}></div>
        <div className="settings-popup__content">
          <h2>Configurações</h2>
          <p className="settings-popup__message">{message}</p>
          <button onClick={handleCreateDefaultConfig} disabled={loading} className="settings-popup__create-button">
            {loading ? "Criando..." : "Criar Configuração Padrão"}
          </button>
        </div>
      </div>
    );
  }

  /** TABS RENDER */
  const renderTabs = () => {
    return (
      <div className="settings-popup__tabs">
        {authUser.isSuperAdmin && (
          <div className={`settings-popup__tab ${activeTab === "superadmin" ? "settings-popup__tab--active" : ""}`} onClick={() => setActiveTab("superadmin")}>
            <FaCogs />
            <span>Super Admin</span>
          </div>
        )}
        <div className={`settings-popup__tab ${activeTab === "schedule" ? "settings-popup__tab--active" : ""}`} onClick={() => setActiveTab("schedule")}>
          <FaClock />
          <span>Horários</span>
        </div>
        <div className={`settings-popup__tab ${activeTab === "holidays" ? "settings-popup__tab--active" : ""}`} onClick={() => setActiveTab("holidays")}>
          <FaCalendarAlt />
          <span>Feriados</span>
        </div>
      </div>
    );
  };

  // SUPER ADMIN TAB
  const renderSuperAdminTab = () => {
    if (!authUser.isSuperAdmin) return null;
    return (
      <div>
        <h3>Configurações de Super Admin</h3>
        {/* Bot Active */}
        <div className="settings-popup__form-group settings-popup__checkbox-group">
          <label>Bot Ativo:</label>
          <input type="checkbox" checked={config.botActive || false} onChange={(e) => setConfig((prev) => ({ ...prev, botActive: e.target.checked }))} />
          <div className="settings-popup__usage">
            <label>Utilização do Bot:</label>
            <div className="settings-popup__usage-container">
              <span className="settings-popup__usage-bar">
                <div className="settings-popup__usage-fill" style={{ width: `${usagePercentage}%` }}></div>
              </span>
              <br />
              <span className="settings-popup__usage-text">
                {formatEuro(usage.used).toLocaleString()} / {formatEuro(usage.total).toLocaleString()} ({usagePercentage}%)
              </span>
            </div>
          </div>
        </div>

        <div className="settings-popup__form-group">
          <label>Hora de Fecho Automático de Conversas:</label>
          <div className="settings-popup__input-group">
            <input type="time" name="closingTime" value={decimalTimeToHHMM(config.closingTime ?? 17.5)} onChange={handleTimeChange} />
            {confirmationState["close"]?.countdown > 0 ? (
              <button disabled className="settings-popup__execute-button countdown">
                Confirmar ({confirmationState["close"].countdown})
              </button>
            ) : confirmationState["close"]?.confirmed ? (
              <button onClick={() => handleExecuteTask("close")} disabled={loading} className="settings-popup__execute-button confirm">
                Confirmar
              </button>
            ) : (
              <button onClick={() => handleExecuteTask("close")} disabled={loading} className="settings-popup__execute-button">
                Executar agora
              </button>
            )}
          </div>
        </div>

        <div className="settings-popup__form-group">
          <label>Hora de Remoção Automática de Conversas:</label>
          <div className="settings-popup__input-group">
            <input type="time" name="deletionTime" value={decimalTimeToHHMM(config.deletionTime ?? 18.0)} onChange={handleTimeChange} />
            {confirmationState["delete"]?.countdown > 0 ? (
              <button disabled className="settings-popup__execute-button countdown">
                Confirmar ({confirmationState["delete"].countdown})
              </button>
            ) : confirmationState["delete"]?.confirmed ? (
              <button onClick={() => handleExecuteTask("delete")} disabled={loading} className="settings-popup__execute-button confirm">
                Confirmar
              </button>
            ) : (
              <button onClick={() => handleExecuteTask("delete")} disabled={loading} className="settings-popup__execute-button">
                Executar agora
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // SCHEDULE TAB
  const renderScheduleTab = () => {
    return (
      <div>
        <h3>Horários e Fechar Interação</h3>
        {/* Working Hours */}
        <div className="settings-popup__form-group">
          <label>Horário de Funcionamento:</label>
          {config.workingHours?.length > 0 ? (
            config.workingHours.map((range, idx) => (
              <div key={idx} className="settings-popup__time-range">
                <input type="time" value={decimalTimeToHHMM(range[0])} onChange={(e) => handleWorkingHoursChange(e, idx, 0)} disabled={!canEditConfig} />
                <span> - </span>
                <input type="time" value={decimalTimeToHHMM(range[1])} onChange={(e) => handleWorkingHoursChange(e, idx, 1)} disabled={!canEditConfig} />
                {canEditConfig && <button onClick={() => removeTimeRange(idx)}>Remover</button>}
              </div>
            ))
          ) : (
            <p>Nenhum horário de funcionamento definido.</p>
          )}
          {canEditConfig && (
            <button onClick={addTimeRange} className="add-time-range">
              Adicionar Horário
            </button>
          )}
        </div>

        {canEditConfig && (
          <div className="settings-popup__form-group">
            <label>Data até quando ficar offline: (Deixe vazio para desativar) </label>
            <input type="date" value={closeUntilDate} onChange={(e) => setCloseUntilDate(e.target.value)} />
          </div>
        )}
      </div>
    );
  };

  // HOLIDAYS TAB
  const renderHolidaysTab = () => {
    return (
      <div>
        <h3>Configurações de Feriados</h3>
        <div className="settings-popup__holiday-buttons">
          <button onClick={resetToDefaultHolidays} className="change-button">
            Obter Configuração Padrão
          </button>
        </div>

        <h3>Tolerâncias de Ponto</h3>
        <div className="settings-popup__holiday-buttons">
          {canEditConfig && (
            <button onClick={handleAddTolerance} className="tolerance-button">
              Adicionar Tolerância
            </button>
          )}
        </div>

        <ul>
          {getTolerances().map(({ holiday, index }) => (
            <li key={index}>
              <input type="date" value={holiday.date} onChange={(e) => updateHoliday(index, "date", e.target.value)} disabled={!canEditConfig} />
              <input
                type="text"
                value={holiday.name || ""}
                onChange={(e) => updateHoliday(index, "name", e.target.value)}
                disabled={!canEditConfig}
                placeholder="Descrição"
              />
              <select value={holiday.type} onChange={(e) => updateHoliday(index, "type", e.target.value)} disabled={!canEditConfig}>
                <option value="T">Tolerância</option>
                <option value="F">Feriado Fixo</option>
                <option value="V">Feriado Variável</option>
                <option value="M">Feriado Municipal</option>
              </select>
              {canEditConfig && <button onClick={() => handleRemoveHoliday(index)}>Remover</button>}
            </li>
          ))}
        </ul>

        <h3>Feriados Fixos</h3>
        <ul>
          {getFixedHolidays().map((holiday, i) => (
            <li key={i}>
              <input type="date" value={holiday.date} onChange={(e) => updateHoliday(i, "date", e.target.value)} disabled={!canEditConfig} />
              <input
                type="text"
                value={holiday.name || ""}
                onChange={(e) => updateHoliday(i, "name", e.target.value)}
                disabled={!canEditConfig}
                placeholder="Nome"
              />
              <select value={holiday.type} onChange={(e) => updateHoliday(i, "type", e.target.value)} disabled={!canEditConfig}>
                <option value="F">Feriado Fixo</option>
                <option value="V">Feriado Variável</option>
                <option value="M">Feriado Municipal</option>
                <option value="T">Tolerância</option>
              </select>
              {canEditConfig && <button onClick={() => handleRemoveHoliday(i)}>Remover</button>}
            </li>
          ))}
        </ul>

        <h3>Feriados Variáveis</h3>
        <ul>
          {getVariableHolidays().map(({ holiday, index }) => (
            <li key={index}>
              <input type="date" value={holiday.date} onChange={(e) => updateHoliday(index, "date", e.target.value)} disabled={!canEditConfig} />
              <input
                type="text"
                value={holiday.name}
                onChange={(e) => updateHoliday(index, "name", e.target.value)}
                disabled={!canEditConfig}
                placeholder="Nome"
              />
              <select value={holiday.type} onChange={(e) => updateHoliday(index, "type", e.target.value)} disabled={!canEditConfig}>
                <option value="V">Feriado Variável</option>
                <option value="F">Feriado Fixo</option>
                <option value="M">Feriado Municipal</option>
                <option value="T">Tolerância</option>
              </select>
              {canEditConfig && <button onClick={() => handleRemoveHoliday(index)}>Remover</button>}
            </li>
          ))}
        </ul>

        <h3>Feriados Locais</h3>
        <ul>
          {getLocalHolidays().map(({ holiday, index }) => (
            <li key={index}>
              <input type="date" value={holiday.date} onChange={(e) => updateHoliday(index, "date", e.target.value)} disabled={!canEditConfig} />
              <input
                type="text"
                value={holiday.name}
                onChange={(e) => updateHoliday(index, "name", e.target.value)}
                disabled={!canEditConfig}
                placeholder="Nome"
              />
              <select value={holiday.type} onChange={(e) => updateHoliday(index, "type", e.target.value)} disabled={!canEditConfig}>
                <option value="M">Feriado Municipal</option>
                <option value="F">Feriado Fixo</option>
                <option value="V">Feriado Variável</option>
                <option value="T">Tolerância</option>
              </select>
              {canEditConfig && <button onClick={() => handleRemoveHoliday(index)}>Remover</button>}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="settings-popup">
      <div className="settings-popup__overlay" onClick={onClose}></div>
      <div className="settings-popup__content">
        <h2>Configurações</h2>

        {renderTabs()}

        <div className="settings-popup__tab-body">
          {activeTab === "superadmin" && renderSuperAdminTab()}
          {activeTab === "schedule" && renderScheduleTab()}
          {activeTab === "holidays" && renderHolidaysTab()}
        </div>

        <div className="settings-popup__buttons">
          {activeTab === "superadmin" || activeTab === "schedule" ? (
            <button className="save-button" onClick={handleUpdateConfig} disabled={loading}>
              Guardar Alterações
            </button>
          ) : (
            <button onClick={saveHolidays} className="save-button" disabled={loading}>
              Guardar Feriados
            </button>
          )}
        </div>
        {message && <p className="settings-popup__message">{message}</p>}
      </div>
    </div>
  );
};

export default SettingsPopup;
